import { useCallback, useEffect, useRef, useState } from 'react';
import {
  readCachedDirections,
  writeCachedDirections,
  type RouteDirectionsResult,
} from '../core/maps/routeDirections';
import { stopHasNavigableCoords, type RouteStop } from '../core/models/RouteStop';
import {
  DEFAULT_OFF_ROUTE_METERS,
  isOffRoute,
  shouldRaiseIdleAlert,
  type LatLng,
} from '../core/rules/routeNavigation';
import { sortStopsBySequence } from '../core/rules/routeOptimize';
import { getRouteDirections, reportRouteIdleAlert } from '../services/firebase/FunctionsService';

type IdleOpts = {
  houseId?: string;
  runnerName?: string;
  idleAlertMin?: number;
  online?: boolean;
};

const IDLE_QUEUE_KEY = 'alea.runner.idleAlertQueue';

function flushIdleQueue(online: boolean) {
  if (!online) return;
  try {
    const raw = localStorage.getItem(IDLE_QUEUE_KEY);
    if (!raw) return;
    const items = JSON.parse(raw) as Array<Record<string, unknown>>;
    localStorage.removeItem(IDLE_QUEUE_KEY);
    for (const item of items) {
      void reportRouteIdleAlert({ ...item, queued: true });
    }
  } catch {
    // ignore
  }
}

/**
 * In-app turn-by-turn: GPS follow, directions fetch/cache, off-route re-route, idle alert.
 */
export function useInAppRouteNav(
  stops: RouteStop[],
  opts: IdleOpts & { enabled?: boolean; active?: boolean },
) {
  const [routeLine, setRouteLine] = useState<RouteDirectionsResult | null>(() => readCachedDirections());
  const [follow, setFollow] = useState<(LatLng & { bearing?: number }) | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [offlineBanner, setOfflineBanner] = useState(false);
  const [rerouting, setRerouting] = useState(false);
  const [navError, setNavError] = useState<string | null>(null);
  const lastProgressAt = useRef<number | null>(null);
  const lastProgressMeters = useRef(0);
  const idleSentRef = useRef(false);
  const watchId = useRef<number | null>(null);

  const remainingStops = sortStopsBySequence(stops).filter(
    (s) => s.status !== 'cleared' && s.status !== 'skipped' && stopHasNavigableCoords(s),
  );

  const fetchDirections = useCallback(async (from?: LatLng | null) => {
    const waypoints: Array<{ lng: number; lat: number }> = [];
    if (from) waypoints.push({ lng: from.lng, lat: from.lat });
    for (const s of remainingStops) {
      waypoints.push({ lng: Number(s.lng), lat: Number(s.lat) });
    }
    if (waypoints.length < 2) return null;

    if (opts.online === false) {
      const cached = readCachedDirections();
      setRouteLine(cached);
      setOfflineBanner(true);
      return cached;
    }

    setRerouting(true);
    setNavError(null);
    try {
      const data = await getRouteDirections({
        houseId: opts.houseId,
        waypoints,
        stopIds: remainingStops.map((s) => s.id),
      }) as RouteDirectionsResult;
      const parsed = {
        ...data,
        fetchedAt: data.fetchedAt ?? Date.now(),
        profile: 'driving' as const,
        stopIds: remainingStops.map((s) => s.id),
      };
      writeCachedDirections(parsed);
      setRouteLine(parsed);
      setOfflineBanner(false);
      setStepIndex(0);
      return parsed;
    } catch (err) {
      const cached = readCachedDirections();
      setRouteLine(cached);
      setOfflineBanner(true);
      setNavError(err instanceof Error ? err.message : 'directions_failed');
      return cached;
    } finally {
      setRerouting(false);
    }
  }, [remainingStops, opts.houseId, opts.online]);

  useEffect(() => {
    flushIdleQueue(Boolean(opts.online));
  }, [opts.online]);

  useEffect(() => {
    if (!opts.enabled || !opts.active) {
      if (watchId.current != null && typeof navigator !== 'undefined' && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId.current);
        watchId.current = null;
      }
      return undefined;
    }

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setNavError('geolocation_unavailable');
      return undefined;
    }

    void fetchDirections(follow);

    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        const point = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          bearing: pos.coords.heading != null && Number.isFinite(pos.coords.heading)
            ? pos.coords.heading
            : undefined,
        };
        setFollow(point);

        const line = routeLine?.coordinates ?? [];
        if (line.length >= 2 && opts.online !== false && isOffRoute(point, line, DEFAULT_OFF_ROUTE_METERS)) {
          void fetchDirections(point);
        }

        // Progress: distance to first remaining stop decreasing
        const target = remainingStops[0];
        if (target) {
          const dist = Math.hypot(
            Number(target.lat) - point.lat,
            Number(target.lng) - point.lng,
          ) * 111_000;
          if (lastProgressAt.current == null) {
            lastProgressAt.current = Date.now();
            lastProgressMeters.current = dist;
          } else if (dist < lastProgressMeters.current - 25) {
            lastProgressMeters.current = dist;
            lastProgressAt.current = Date.now();
            idleSentRef.current = false;
          }
        }

        // Advance step banner by proximity
        const steps = routeLine?.steps ?? [];
        if (steps.length > 0) {
          let nearest = stepIndex;
          for (let i = stepIndex; i < steps.length; i += 1) {
            const loc = steps[i].location;
            if (!loc) continue;
            const d = Math.hypot(loc[1] - point.lat, loc[0] - point.lng) * 111_000;
            if (d < 35) nearest = Math.min(i + 1, steps.length - 1);
          }
          if (nearest !== stepIndex) setStepIndex(nearest);
        }

        if (
          !idleSentRef.current
          && shouldRaiseIdleAlert({
            lastProgressAtMs: lastProgressAt.current,
            nowMs: Date.now(),
            idleAlertMin: opts.idleAlertMin,
          })
        ) {
          idleSentRef.current = true;
          const payload = {
            houseId: opts.houseId,
            runnerName: opts.runnerName,
            idleMin: opts.idleAlertMin ?? 10,
            stopName: remainingStops[0]?.name,
          };
          if (opts.online === false) {
            try {
              const q = JSON.parse(localStorage.getItem(IDLE_QUEUE_KEY) || '[]');
              q.push(payload);
              localStorage.setItem(IDLE_QUEUE_KEY, JSON.stringify(q));
            } catch {
              // ignore
            }
          } else {
            void reportRouteIdleAlert(payload);
          }
        }
      },
      () => setNavError('geolocation_denied'),
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 },
    );

    return () => {
      if (watchId.current != null) {
        navigator.geolocation.clearWatch(watchId.current);
        watchId.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- start watch when nav activates
  }, [opts.enabled, opts.active]);

  const nextStep = routeLine?.steps?.[stepIndex] ?? null;

  return {
    routeLine,
    follow,
    nextStep,
    stepIndex,
    offlineBanner,
    rerouting,
    navError,
    remainingStops,
    refreshRoute: () => fetchDirections(follow),
  };
}
